<template>
  <form @submit.prevent="handleSubmit" id="workorder_create_form" enctype="multipart/form-data">
    
    <!-- Work Order Name -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Work Order Name <span class="text-danger">*</span></label>
      <input type="text" class="form-control form-control-thin" id="name" name="name" ref="name"
        v-model="form.name" placeholder="Enter work order name" />
    </div>

    <!-- Requests (Multi-select) -->
    <div class="mb-3 form-group">
      <MultiSelect
        v-model="form.requests"
        :options="custom_requests"
        option-label="name"
        option-value="id"
        :multiple="true"
        label="Requests"
        placeholder="Select Requests"
      />
    </div>

    <!-- Categories (Multi-select) -->
    <div class="mb-3 form-group">
      <MultiSelect
        v-model="form.categories"
        :options="categories"
        option-label="id"
        option-value="id"
        :multiple="true"
        label="Categories"
        placeholder="Select Categories"
      />
      <div data-ie="categories"></div>
    </div>

    <!-- Location -->
    <div class="mb-4">
      <MultiSelect
        v-model="form.location"
        :options="locations"
        option-label="name"
        option-value="id"
        :multiple="false"
        label="Location"
        placeholder="Select Location"
      />
      <div data-ie="location"></div>
    </div>

    <!-- Template -->
    <div class="mb-3">
      <MultiSelect
        v-model="form.template"
        :options="templates"
        option-label="name"
        option-value="id"
        :multiple="false"
        label="Checklist"
        placeholder="Select Checklist Template"
      />
      <div data-ie="template"></div>
    </div>

    <!-- Work Type -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Work Type <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.work_type" name="work_type" ref="work_type">
        <option value="" disabled>Select Work Type</option>
        <option value="preventive">Preventive</option>
        <option value="corrective">Corrective</option>
        <option value="reactive">Reactive</option>
      </select>
    </div>

    <!-- Priority -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Priority <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.priority" name="priority" ref="priority">
        <option value="" disabled>Select Priority</option>
        <option value="low">🔵 Low</option>
        <option value="medium">🟡 Medium</option>
        <option value="high">🔴 High</option>
      </select>
    </div>

    <!-- Assign To -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Assign To <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.assign_to" name="assign_to" ref="assign_to">
        <option value="" disabled>Select</option>
        <option value="team">Team</option>
        <option value="individual">Individual</option>
        <option value="vendor">Vendor/Contractor</option>
      </select>
    </div>

    <!-- Team -->
    <div class="mb-3" v-if="form.assign_to === 'team'">
      <MultiSelect
        v-model="form.team"
        :options="teams"
        option-label="name"
        option-value="id"
        :multiple="false"
        label="Team"
        placeholder="Select Team"
      />
      <div data-ie="team"></div>
    </div>

    <!-- Individual Users (Multi-select) -->
    <div class="mb-3" v-if="form.assign_to === 'individual'">
      <MultiSelect
        v-model="form.users"
        :options="custom_users"
        option-label="name"
        option-value="id"
        :multiple="true"
        label="Users/Inspectors"
        placeholder="Select Users"
      />
      <div data-ie="users"></div>
    </div>

    <!-- Vendor Selection -->
    <div class="mb-3" v-if="form.assign_to === 'vendor'">
      <MultiSelect
        v-model="form.vendor"
        :options="custom_vendors"
        option-label="name"
        option-value="id"
        :multiple="false"
        label="Select Vendor/Contractor"
        placeholder="Select Vendor/Contractor"
        @update:model-value="onVendorChange"
      />
      <div data-ie="vendor"></div>
    </div>

    <!-- Vendor Contacts (sub-dropdown) -->
    <div class="mb-3" v-if="form.assign_to === 'vendor' && form.vendor && vendorContacts.length > 0">
      <label class="form-label form-label-thin">Select Contact <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.vendor_contact" name="vendor_contact" ref="vendor_contact">
        <option value="" disabled>Select Contact</option>
        <option v-for="contact in vendorContacts" :key="contact.id" :value="contact.id">
          {{ contact.name || '' }} ({{ contact.email || '' }})
        </option>
      </select>
      <small class="text-muted">Select a contact person for this vendor</small>
    </div>

    <!-- Assign Verifier -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Assign Verifier <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.verifier" name="verifier" ref="verifier">
        <option value="" selected disabled>Select Verifier</option>
        <option v-for="user in users" :key="user.id" :value="user.id">
          {{ user.first_name }} {{ user.last_name }}
        </option>
      </select>
    </div>

    <!-- Frequency -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Frequency <span class="text-danger">*</span></label>
      <select class="form-select form-select-thin" v-model="form.frequency" name="frequency" ref="frequency">
        <option value="" disabled>Select Frequency</option>
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
        <option value="yearly">Yearly</option>
        <option value="custom">Custom</option>
      </select>
    </div>

    <!-- From Date - Show when frequency is NOT custom -->
    <div v-if="form.frequency && form.frequency !== 'custom'" class="mb-3">
      <label class="form-label form-label-thin">From Date <span class="text-danger">*</span></label>
      <input type="date" :min="new Date().toISOString().split('T')[0]" class="form-control form-control-thin" v-model="form.from_date" name="from_date" style="max-width: 200px; font-size: 12px;" />
    </div>

    <!-- Frequency Options Container -->
    <div v-if="form.frequency" class="mb-3 p-3 rounded" style="background: #f5f5f5; font-size: 13px;">
      
      <!-- Daily Frequency -->
      <div v-if="form.frequency === 'daily'">
        <input type="hidden" name="freq" value="daily">
        <div class="fw-bold mb-2" style="font-size: 13px;">Everyday</div>
        <div class="d-flex flex-wrap form-group gap-2">
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Sunday"> Sunday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Monday"> Monday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Tuesday"> Tuesday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Wednesday"> Wednesday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Thursday"> Thursday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Friday"> Friday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.exclude" value="Saturday"> Saturday
          </label>
        </div>
      </div>

      <!-- Weekly Frequency -->
      <div v-if="form.frequency === 'weekly'">
        <input type="hidden" name="freq" value="weekly">
        <div class="fw-bold mb-2" style="font-size: 13px;">Weekly</div>
        <div class="d-flex flex-wrap form-group gap-2">
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Monday"> Monday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Tuesday"> Tuesday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Wednesday"> Wednesday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Thursday"> Thursday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Friday"> Friday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Saturday"> Saturday
          </label>
          <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
            <input type="checkbox" v-model="form.days" name="days" value="Sunday"> Sunday
          </label>
        </div>
      </div>

      <!-- Monthly Frequency -->
      <div v-if="form.frequency === 'monthly'">
        <input type="hidden" name="freq" value="monthly">
        <div class="fw-bold mb-2" style="font-size: 13px;">Monthly</div>
        <div class="mb-2">
          <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Week:</div>
          <div class="d-flex flex-wrap form-group gap-2 mt-1">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 5" :key="i">
              <input type="checkbox" v-model="form.weeks" :value="i"> Week {{ i }}
            </label>
          </div>
        </div>
        <div>
          <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Days:</div>
          <div class="d-flex flex-wrap form-group gap-2 mt-1">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Monday"> Monday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Tuesday"> Tuesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Wednesday"> Wednesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Thursday"> Thursday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Friday"> Friday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Saturday"> Saturday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Sunday"> Sunday
            </label>
          </div>
        </div>
      </div>

      <!-- Yearly Frequency -->
      <div v-if="form.frequency === 'yearly'">
        <input type="hidden" name="freq" value="yearly">
        <div class="fw-bold mb-2" style="font-size: 13px;">Yearly</div>
        <div class="mb-2">
          <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Month:</div>
          <div class="d-flex flex-wrap form-group gap-1 mt-1">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 12" :key="i">
              <input type="checkbox" v-model="form.months" :value="i"> M{{ i }}
            </label>
          </div>
        </div>
        <div class="mb-2">
          <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Week:</div>
          <div class="d-flex flex-wrap form-group gap-2 mt-1">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 5" :key="i">
              <input type="checkbox" v-model="form.weeks" :value="i"> Week {{ i }}
            </label>
          </div>
        </div>
        <div>
          <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Days:</div>
          <div class="d-flex flex-wrap form-group gap-2 mt-1">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Monday"> Monday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Tuesday"> Tuesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Wednesday"> Wednesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Thursday"> Thursday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Friday"> Friday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Saturday"> Saturday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" value="Sunday"> Sunday
            </label>
          </div>
        </div>
      </div>

      <!-- Custom Frequency -->
      <div v-if="form.frequency === 'custom'">
        <div class="fw-bold mb-2" style="font-size: 13px;">Schedule On</div>
        <div class="form-group">
          <input type="date" class="form-control form-control-thin" v-model="form.schedule_date" name="schedule_date" style="max-width: 200px; font-size: 12px;" />
        </div>
      </div>

      <!-- Times - Show for all frequencies -->
      <div v-if="form.frequency">
        <div class="fw-bold mt-3 mb-2" style="font-size: 13px;">Times</div>
        <div id="time-container" class="row"></div>
        <div data-ie="times"></div>
        <button type="button" @click="addTimeInput" class="btn btn-sm btn-success mt-2" style="font-size: 12px;">
          <i class="fas fa-clock"></i> Add Time
        </button>
      </div>
    </div>

    <!-- Assets (Multi-select) -->
    <div class="mb-3">
      <MultiSelect
        v-model="form.assets"
        :options="assets"
        option-label="name"
        option-value="id"
        :multiple="true"
        label="Asset"
        placeholder="Select Asset"
      />
    </div>

    <!-- Description -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Description</label>
      <textarea class="form-control form-control-thin" id="description" name="description" ref="description"
        v-model="form.description" placeholder="Enter description" rows="3"></textarea>
    </div>

    <!-- File Upload -->
    <div class="mb-4">
      <FileUploader v-model:files="form.files" maxFiles="5" 
        :help-text="'Upload related documents or images. Max 5 files.'" />
    </div>

    <!-- Modal Footer -->
    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary hover-white" @click="close">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button type="submit" class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn">
        <i class="fa-solid fa-plus fs-12"></i> Create
      </button>
    </div>
  </form>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'
import FileUploader from '@/components/FileUploader.vue'

export default {
  name: 'CreateWorkOrder',
  emits: ['created', 'close'],
  components: {
    MultiSelect,
    FileUploader
  },
  data() {
    return {
      form: {
        name: '',
        location: '',
        template: '',
        work_type: '',
        priority: '',
        assign_to: '',
        team: '',
        users: [],
        vendor: '',
        vendor_contact: '',
        verifier: '',
        frequency: '',
        from_date: '',
        assets: [],
        categories: [],
        requests: [],
        description: '',
        exclude: [],
        days: [],
        weeks: [],
        months: [],
        times: [],
        schedule_date: '',
        files: null
      },
      submitting: false,
      locations: [],
      templates: [],
      teams: [],
      users: [],
      vendors: [],
      assets: [],
      categories: [],
      requests: [],
      custom_requests:[],
      custom_users:[],
      custom_vendors:[],
      vendorContacts: [],
    }
  },
  mounted() {
    this.getDropdownData()
  },
  watch: {
    'form.frequency': function(newVal) {
      // Reset frequency related fields when frequency changes
      if (newVal !== 'daily') this.form.exclude = []
      if (newVal !== 'weekly') this.form.days = []
      if (newVal !== 'monthly') {
        this.form.days = []
        this.form.weeks = []
      }
      if (newVal !== 'yearly') {
        this.form.days = []
        this.form.weeks = []
        this.form.months = []
      }
      // Clear time container when frequency changes
      const container = document.getElementById('time-container')
      if (container) {
        container.innerHTML = ''
      }
    }
  },
  methods: {
    onVendorChange() {
      // Reset contact selection
      this.form.vendor_contact = ''
      this.vendorContacts = []
      
      // Find selected vendor and get its contacts
      if (this.form.vendor) {
        const vendor = this.vendors.find(v => v.id === this.form.vendor)
        if (vendor && vendor.get_contacts) {
          this.vendorContacts = vendor.get_contacts
        }
      }
    },

    async handleSubmit() {
      this.submitting = true

      // Filter out empty times before submitting
      const times = this.form.times.filter(t => t !== '' && t !== null && t !== undefined)
      
      // Create a FormData object for file upload
      const formData = new FormData()
      
      // Append all form fields
      formData.append('name', this.form.name || '')
      formData.append('location', this.form.location || '')
      formData.append('template', this.form.template || '')
      formData.append('work_type', this.form.work_type || '')
      formData.append('priority', this.form.priority || '')
      formData.append('assign_to', this.form.assign_to || '')
      formData.append('team', this.form.team || '')
      formData.append('vendor', this.form.vendor || '')
      formData.append('vendor_contact', this.form.vendor_contact || '')
      formData.append('verifier', this.form.verifier || '')
      formData.append('frequency', this.form.frequency || '')
      formData.append('from_date', this.form.from_date || '')
      formData.append('description', this.form.description || '')
      formData.append('schedule_date', this.form.schedule_date || '')
      
      // Append arrays as individual entries
      if (this.form.users && this.form.users.length > 0) {
        this.form.users.forEach(userId => {
          formData.append('users[]', userId)
        })
      }
      
      if (this.form.assets && this.form.assets.length > 0) {
        this.form.assets.forEach(assetId => {
          formData.append('assets[]', assetId)
        })
      }
      
      if (this.form.categories && this.form.categories.length > 0) {
        this.form.categories.forEach(categoryId => {
          formData.append('categories[]', categoryId)
        })
      }
      
      if (this.form.requests && this.form.requests.length > 0) {
        this.form.requests.forEach(requestId => {
          formData.append('requests[]', requestId)
        })
      }
      
      if (this.form.exclude && this.form.exclude.length > 0) {
        this.form.exclude.forEach(excludeDay => {
          formData.append('exclude[]', excludeDay)
        })
      }
      
      if (this.form.days && this.form.days.length > 0) {
        this.form.days.forEach(day => {
          formData.append('days[]', day)
        })
      }
      
      if (this.form.weeks && this.form.weeks.length > 0) {
        this.form.weeks.forEach(week => {
          formData.append('weeks[]', week)
        })
      }
      
      if (this.form.months && this.form.months.length > 0) {
        this.form.months.forEach(month => {
          formData.append('months[]', month)
        })
      }
      
      if (times && times.length > 0) {
        times.forEach(time => {
          formData.append('times[]', time)
        })
      }
      
      // Append files if they exist
      if (this.form.files && this.form.files.length > 0) {
        for (let i = 0; i < this.form.files.length; i++) {
          formData.append('files[]', this.form.files[i])
        }
      }

      const result = await form_api_submit({
        url: 'wo/create',
        method: 'POST',
        data: formData,
        take_form_data: false,
        curr_form_id: '#workorder_create_form',
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (result.success) {
        this.$emit('created')
        this.close()
        this.resetForm()
      }

      this.submitting = false
    },

    addTimeInput() {
      const container = document.getElementById('time-container')
      if (!container) return

      const row = document.createElement('div')
      row.className = 'row mb-1'

      const col = document.createElement('div')
      col.className = 'col-md-4'

      const inputGroup = document.createElement('div')
      inputGroup.className = 'input-group'

      const input = document.createElement('input')
      input.type = 'time'
      input.name = 'times[]'
      input.className = 'form-control form-control-thin'
      input.placeholder = 'Select time'
      input.style.fontSize = '12px'

      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className = 'btn btn-outline-danger btn-sm'
      removeBtn.innerHTML = 'X'
      removeBtn.style.fontSize = '12px'
      removeBtn.onclick = () => {
        row.remove()
        // Remove the value from times array when removed
        const val = input.value
        if (val) {
          const index = this.form.times.indexOf(val)
          if (index > -1) {
            this.form.times.splice(index, 1)
          }
        }
      }

      inputGroup.appendChild(input)
      inputGroup.appendChild(removeBtn)
      col.appendChild(inputGroup)
      row.appendChild(col)
      container.appendChild(row)

      // When input changes, update the times array
      input.addEventListener('change', (e) => {
        const val = e.target.value
        // Get all time inputs
        const inputs = container.querySelectorAll('input[type="time"]')
        const currentIndex = Array.from(inputs).indexOf(input)
        
        // Update or add the value at the correct index
        if (this.form.times[currentIndex] !== undefined) {
          this.form.times[currentIndex] = val
        } else {
          this.form.times.push(val)
        }
      })
      
      // Add an initial empty value to times array
      this.form.times.push('')
    },

    async getDropdownData() {
      const result = await form_api_submit({
        url: 'wo/dropdowns',
        method: 'GET',
        show_success_toast: false
      })
      if (result.success) {
        this.requests = result.data.requests
        this.custom_requests = (this.requests || []).map(request => ({
            id: request.id,
            name: request.title || 'Unnamed Request',
          }))
        this.templates = result.data.templates
        this.teams = result.data.teams
        this.users = result.data.users
        this.vendors = result.data.vendors || []
        this.assets = result.data.assets
        this.categories = result.data.categories
        this.locations = result.data.locations

        this.custom_users = (this.users || []).map(user => ({
            id: user.id,
            name: user.first_name +" " + user.last_name ,
          }))

        this.custom_vendors = (this.vendors || []).map(vendor => ({
            id: vendor.id,
            name: vendor.first_name,
          }))
      }
    },

    close() {
      this.$emit('close')
    },

    resetForm() {
      this.form = {
        name: '',
        location: '',
        template: '',
        work_type: '',
        priority: '',
        assign_to: '',
        team: '',
        users: [],
        vendor: '',
        vendor_contact: '',
        verifier: '',
        frequency: '',
        from_date: '',
        assets: [],
        categories: [],
        requests: [],
        description: '',
        exclude: [],
        days: [],
        weeks: [],
        months: [],
        times: [],
        schedule_date: '',
        files: null
      }
      this.vendorContacts = []
    }
  }
}
</script>

<style scoped>
.form-label-thin {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.form-control-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-control-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-select-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-select-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}
</style>